import type { Unsafe } from "../../../shared/unsafe";

export type Done<TBase> = {
	<TP extends TBase>(
		page: TP,
		onNonInteractive: keyof TP,
		options?: { skip?: boolean },
	): void;
	isLocked: boolean;
};

/**
 * Builds the shared `done` terminator used by the refund/purchase orchestrators.
 * Any call cleans up everything (`dispose`) and is idempotent via `isLocked`:
 * the first call wins, later calls no-op. When `skip` resolves true (explicit, or
 * a non-interactive request) the page's `onNonInteractive` handler runs instead of
 * rendering the page. Callers reset `done.isLocked = false` to re-arm it.
 */
export function createDone<TBase>(deps: {
	dispose: () => void;
	setPage: (page: TBase) => void;
	interactive: boolean;
}): Done<TBase> {
	const done = (<TP extends TBase>(
		page: TP,
		onNonInteractive: keyof TP,
		options?: { skip?: boolean },
	): void => {
		// Any "done" call should close and cleanup everything all the time.
		deps.dispose();

		if (done.isLocked) {
			return;
		} else {
			done.isLocked = true;
		}

		// Default to `skip: true` if `skip` undefined and request is not interactive.
		const skip = options?.skip ?? !deps.interactive;

		return void (skip
			? (page[onNonInteractive] as Unsafe<() => void>)()
			: deps.setPage(page));
	}) as Done<TBase>;
	done.isLocked = false;
	return done;
}
